import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, DollarSign, Building, Eye, CheckCircle, Clock, Filter, Users, XCircle, ArrowRightLeft, User } from 'lucide-react';
import { MarketplaceDeal } from '@/hooks/useMarketplaceDeals';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useDealApplications } from '@/hooks/useDealApplications';
import { useUser } from '@/contexts/UserContext';
import { CounterOfferModal } from './CounterOfferModal';
import { DealApplicationModal } from './DealApplicationModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface MarketplaceDealCardProps {
  deal: MarketplaceDeal;
  onViewDetails: (deal: MarketplaceDeal) => void;
  onClaimDeal: (dealId: string) => Promise<{ success: boolean; applicationId?: string }>;
  userCanClaim: boolean;
  onApplicationRevoked?: () => void;
}

export const MarketplaceDealCard: React.FC<MarketplaceDealCardProps> = ({ 
  deal, 
  onViewDetails, 
  onClaimDeal,
  userCanClaim,
  onApplicationRevoked
}) => {
  const [claiming, setClaiming] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<string | null>(null);
  const [applicantCount, setApplicantCount] = useState(0);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [responding, setResponding] = useState(false);
  const [showCounterOffer, setShowCounterOffer] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [defaultProposeDifferent, setDefaultProposeDifferent] = useState(false);
  const { toast } = useToast();
  const { currentUser, accountType } = useUser();
  const { checkMyApplicationStatus, applications, revokeApplication, acceptApplication, rejectApplication } = useDealApplications(deal.id);

  // Sync local state with deal prop changes
  useEffect(() => {
    if (deal.user_application_status) {
      setApplicationStatus(deal.user_application_status);
    } else {
      setApplicationStatus(null);
    }
  }, [deal.user_application_status]);

  useEffect(() => {
    if (deal.status === 'available') {
      setApplicantCount(applications.length);
    }
  }, [applications, deal.status]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'available': return 'bg-green-500/10 text-green-700 border-green-200';
      case 'pending': return 'bg-yellow-500/10 text-yellow-700 border-yellow-200';
      case 'active': return 'bg-blue-500/10 text-blue-700 border-blue-200';
      case 'completed': return 'bg-purple-500/10 text-purple-700 border-purple-200';
      default: return 'bg-gray-500/10 text-gray-700 border-gray-200';
    }
  };

  const getDealTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'sponsorship': return 'bg-blue-500/10 text-blue-700 border-blue-200';
      case 'endorsement': return 'bg-purple-500/10 text-purple-700 border-purple-200';
      case 'partnership': return 'bg-green-500/10 text-green-700 border-green-200';
      case 'appearance': return 'bg-orange-500/10 text-orange-700 border-orange-200';
      default: return 'bg-gray-500/10 text-gray-700 border-gray-200';
    }
  };

  const handleClaimDeal = async (dealId: string) => {
    const result = await onClaimDeal(dealId);
    if (result.success) {
      setApplicationStatus('pending');
    }
    return result;
  };

  const handleRevokeApplication = async () => {
    if (revoking) return;
    
    setRevoking(true);
    try {
      const result = await revokeApplication(deal.id);
      if (result.success) {
        setApplicationStatus(null);
        setShowRevokeDialog(false);
        // Notify parent to refresh the deals list
        onApplicationRevoked?.();
      }
    } catch (error: any) {
      console.error('Revoke application error:', error);
    } finally {
      setRevoking(false);
    }
  };

  const handleAcceptPairing = async () => {
    if (!deal.user_application_id || responding) return;
    
    setResponding(true);
    try {
      const result = await acceptApplication(deal.user_application_id);
      if (result.success) {
        setApplicationStatus('accepted');
        toast({
          title: "Deal Accepted!",
          description: "You've accepted this partnership opportunity.",
        });
        onApplicationRevoked?.(); // Refresh the list
      }
    } catch (error: any) {
      console.error('Accept pairing error:', error);
      toast({
        title: "Failed to Accept",
        description: error.message || "There was an error accepting the deal.",
        variant: "destructive",
      });
    } finally {
      setResponding(false);
    }
  };

  const handleRejectPairing = async () => {
    if (!deal.user_application_id || responding) return;
    
    setResponding(true);
    try {
      const result = await rejectApplication(deal.user_application_id);
      if (result.success) {
        setApplicationStatus('rejected');
        toast({
          title: "Deal Declined",
          description: "You've declined this partnership opportunity.",
        });
        onApplicationRevoked?.(); // Refresh the list
      }
    } catch (error: any) {
      console.error('Reject pairing error:', error);
      toast({
        title: "Failed to Decline",
        description: error.message || "There was an error declining the deal.",
        variant: "destructive",
      });
    } finally {
      setResponding(false);
    }
  };

  const isExpired = deal.deadline && new Date(deal.deadline) < new Date();

  return (
    <Card className={`hover:shadow-lg transition-shadow ${isExpired ? 'opacity-75' : ''}`}>
      <CardHeader className="pb-3">
        {/* Mobile: badges sit on a row above the title so a long title can use full width.
            Desktop (sm+): original side-by-side title / vertical badges. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-start">
          <div className="flex flex-row sm:flex-col gap-2 order-first sm:order-last flex-shrink-0">
            <Badge variant="secondary" className={getStatusColor(deal.status)}>
              {deal.status}
            </Badge>
            <Badge variant="outline" className={getDealTypeColor(deal.deal_type)}>
              {deal.deal_type}
            </Badge>
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg font-semibold mb-2">{deal.title}</CardTitle>
            {deal.brand_name ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Building className="h-4 w-4" />
                <span className="truncate">{deal.brand_name}</span>
              </div>
            ) : null}
          </div>
        </div>
        
        {deal.description && (
          <p className="text-sm text-muted-foreground line-clamp-3">
            {deal.description}
          </p>
        )}

        {/* Show criteria badges if they exist */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {deal.status === 'available' && applicantCount > 0 && (
            <Badge variant="secondary" className="text-xs flex items-center gap-1">
              <Users className="h-3 w-3" />
              {applicantCount} {applicantCount === 1 ? 'applicant' : 'applicants'}
            </Badge>
          )}
          {deal.eligibility_criteria && Object.keys(deal.eligibility_criteria).length > 0 && (
            <>
              <Badge variant="outline" className="text-xs flex items-center gap-1">
                <Filter className="h-3 w-3" />
                Specific Requirements
              </Badge>
              {deal.eligibility_criteria.sports && deal.eligibility_criteria.sports.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {deal.eligibility_criteria.sports.join(', ')}
                </Badge>
              )}
              {deal.eligibility_criteria.tiers && deal.eligibility_criteria.tiers.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {deal.eligibility_criteria.tiers.join(', ')} tier
                </Badge>
              )}
              {deal.eligibility_criteria.min_followers && (
                <Badge variant="secondary" className="text-xs">
                  {deal.eligibility_criteria.min_followers.toLocaleString()}+ followers
                </Badge>
              )}
            </>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold">${deal.deal_value.toLocaleString()}</span>
          </div>
          
          {deal.deadline && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className={isExpired ? 'text-red-600' : ''}>
                {isExpired ? 'Expired' : format(new Date(deal.deadline), 'MMM dd, yyyy')}
              </span>
            </div>
          )}
        </div>

        {deal.requirements && deal.requirements.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Requirements:</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              {deal.requirements.slice(0, 3).map((req, index) => (
                <li key={index} className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-muted-foreground rounded-full flex-shrink-0" />
                  {req}
                </li>
              ))}
              {deal.requirements.length > 3 && (
                <li className="text-xs italic">+{deal.requirements.length - 3} more requirements...</li>
              )}
            </ul>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(deal)}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              View Details
            </Button>
            
            {applicationStatus === 'pending' ? (
              <Button size="sm" variant="secondary" disabled className="flex items-center gap-2 whitespace-nowrap">
                <Clock className="h-4 w-4" />
                Pending Approval
              </Button>
            ) : applicationStatus === 'accepted' ? (
              <Button size="sm" disabled className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Accepted
              </Button>
            ) : applicationStatus === 'rejected' ? (
              <Button size="sm" variant="secondary" disabled>
                Application Rejected
              </Button>
            ) : deal.status === 'active' && deal.talent_profile_id ? (
              <Button size="sm" disabled className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Active
              </Button>
            ) : deal.status === 'available' && userCanClaim && !isExpired ? (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    setDefaultProposeDifferent(false);
                    setShowApplicationModal(true);
                  }}
                  className="flex items-center gap-2"
                >
                  Apply for Deal
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setDefaultProposeDifferent(true);
                    setShowApplicationModal(true);
                  }}
                  className="flex items-center gap-2"
                >
                  <ArrowRightLeft className="h-4 w-4" />
                  Counter Offer
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="secondary" disabled>
                {isExpired ? 'Expired' : 'Not Available'}
              </Button>
            )}
          </div>

          {/* Revoke Application Button - Only for talent-initiated pending applications */}
          {applicationStatus === 'pending' && deal.application_initiated_by === 'talent' && (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setShowRevokeDialog(true)}
                disabled={revoking}
                className="flex items-center gap-2"
              >
                <XCircle className="h-4 w-4" />
                Revoke Application
              </Button>
            </div>
          )}

          {/* Accept/Reject/Counter Buttons - Only for brand-initiated pending applications */}
          {applicationStatus === 'pending' && deal.application_initiated_by === 'brand' && (
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleRejectPairing}
                disabled={responding}
                className="flex items-center gap-2"
              >
                <XCircle className="h-4 w-4" />
                Decline
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowCounterOffer(true)}
                className="flex items-center gap-2"
              >
                <ArrowRightLeft className="h-4 w-4" />
                Counter
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptPairing}
                disabled={responding}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Accept
              </Button>
            </div>
          )}

          {/* Counter Offer button for talent with pending applications */}
          {applicationStatus === 'pending' && deal.application_initiated_by === 'talent' && accountType === 'talent' && deal.user_application_id && (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowCounterOffer(true)}
                className="flex items-center gap-2"
              >
                <ArrowRightLeft className="h-4 w-4" />
                Counter Offer
              </Button>
            </div>
          )}
        </div>
      </CardContent>

      {/* Counter Offer Modal */}
      {deal.user_application_id && (
        <CounterOfferModal
          isOpen={showCounterOffer}
          onClose={() => setShowCounterOffer(false)}
          dealApplicationId={deal.user_application_id}
          dealId={deal.id}
          currentValue={deal.deal_value}
          currentDealType={deal.deal_type}
          currentDeadline={deal.deadline}
          onSuccess={() => {
            onApplicationRevoked?.();
          }}
        />
      )}

      {/* Deal Application Modal */}
      <DealApplicationModal
        isOpen={showApplicationModal}
        onClose={() => {
          setShowApplicationModal(false);
          setDefaultProposeDifferent(false);
        }}
        deal={deal}
        onApply={handleClaimDeal}
        defaultProposeDifferent={defaultProposeDifferent}
      />

      <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Application?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to withdraw your application for "{deal.title}"? 
              This action cannot be undone, but you can reapply later if the deal is still available.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeApplication}
              disabled={revoking}
              className="bg-red-600 hover:bg-red-700"
            >
              {revoking ? 'Revoking...' : 'Revoke Application'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
